import SuperFetch from 'library/helpers/superFetch';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { assetGroupActions } from './slice';
import { permissionsActions } from 'reduxStore/permissions/slice';

export function* loadGroups() {
	try {
		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile);

		// Get Groups from the current organisation.
		const assetGroups = yield call(() => SuperFetch.get('/deviceGroups'));

		if (assetGroups.statusCode === 200) {
			yield put(assetGroupActions.loadGroupsSuccess({ orgID, data: assetGroups.data }));
		} else {
			throw Error(assetGroups.error.message || assetGroups.error || 'Failed to load Groups.');
		}
	} catch (error) {
		yield put(assetGroupActions.loadGroupsError(error));
	}
}

// 🔥 Actions after load groups success
export function* loadGroupsSuccess() {
	yield put(assetGroupActions.loadAssetsGroups());
}

export function* loadAssetsGroups(action) {
	try {
		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile); // Get orgID

		// Get the assets of the group
		const assets = yield call(() => SuperFetch.get(`/deviceGroups/getAllDeviceGroupsAllDevices`));

		if (assets.statusCode === 200) {
			yield put(assetGroupActions.loadAssetsGroupsSuccess({ orgID, data: assets.data }));
		} else {
			throw Error(assets.error.message || assets.error || 'Failed to load group assets.');
		}
	} catch (error) {
		yield put(assetGroupActions.loadAssetsGroupsError(error));
	}
}

export function* addGroup(action) {
	try {
		const { data } = action.payload;
		const { assetIDs, ...groupData } = data; // Separate assetIDs if present

		// 1️⃣ Create the device group
		const assetGroup = yield call(() => SuperFetch.post(`/deviceGroups`, groupData));

		if (assetGroup.statusCode === 201 || assetGroup.statusCode === 200) {
			const { groupID } = assetGroup.data;

			yield put(assetGroupActions.addGroupSuccess());

			Notification('success', 'Device Group created.');

			// 2️⃣ If assetIDs exist, dispatch action to assign them to the group
			if (Array.isArray(assetIDs) && assetIDs.length > 0) {
				yield put(assetGroupActions.addGroupAsset({ groupID, assetIDs }));
			}
		} else {
			throw new Error(
				assetGroup.error?.message || assetGroup.error || 'Failed to create Device Group. Please try again.'
			);
		}
	} catch (error) {
		yield put(assetGroupActions.addGroupError(error));
		Notification('error', error.message);
	}
}

// 🔥Actions here after add asset group success
export function* addGroupSuccess() {
	yield put(assetGroupActions.loadGroups());
}

export function* updateGroup(action) {
	const { groupID, data } = action.payload;

	try {
		// Update the group
		const assetGroup = yield call(() => SuperFetch.patch(`/deviceGroups/${groupID}`, data));

		if (assetGroup.statusCode === 200) {
			yield put(assetGroupActions.updateGroupSuccess());

			// Show success notification
			Notification('success', 'Group has been updated successfully.');
		} else {
			throw Error(assetGroup.error.message || assetGroup.error || 'Failed to update group.');
		}
	} catch (error) {
		yield put(assetGroupActions.updateGroupError(error));
		Notification('error', error.message);
	}
}

// 🔥Actions here after update Group
export function* updateGroupSuccess() {
	yield put(assetGroupActions.loadGroups());
}

// ✅ Handle single or multiple group deletions
export function* deleteGroup(action) {
	const { groupIDs } = action.payload;

	try {
		// ✅ Call API to delete multiple groups in parallel using redux-saga's all
		const deleteResponses = yield all(
			groupIDs.map((groupID) => call(SuperFetch.delete, `/deviceGroups/${groupID}`))
		);

		// ✅ Check if all deletions were successful
		const allSuccessful = deleteResponses.every((res) => res.statusCode === 200);

		if (allSuccessful) {
			yield put(assetGroupActions.deleteGroupSuccess());
			Notification('success', 'Group(s) have been permanently deleted.');
		} else {
			throw Error(allSuccessful.error.message || allSuccessful.error || 'Failed to delete group(s).');
		}
	} catch (error) {
		yield put(assetGroupActions.deleteGroupError(error));
		Notification('error', error.message);
	}
}

// 🔥 Actions after delete group
export function* deleteGroupSuccess() {
	yield put(assetGroupActions.loadGroups());
	yield put(permissionsActions.loadPermissions());
}

export function* loadGroupAssets(action) {
	try {
		// Get the groupID from the action payload
		const { groupID } = action.payload;

		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile); // Get orgID

		// Get the assets of the group
		const assets = yield call(() => SuperFetch.get(`/deviceGroups/${groupID}/assets`));

		if (assets.statusCode === 200) {
			yield put(assetGroupActions.loadGroupAssetsSuccess({ orgID, groupID, data: assets.data }));
		} else {
			throw Error(assets.error.message || assets.error || 'Failed to load group assets.');
		}
	} catch (error) {
		yield put(assetGroupActions.loadGroupAssetsError(error));
	}
}

export function* addGroupAsset(action) {
	try {
		const { groupID, assetIDs } = action.payload;

		// 1️⃣ Add the assets to the group
		const assets = yield call(() => SuperFetch.post(`/deviceGroups/${groupID}/assets`, { assetIDs }));

		// 2️⃣ If successful, dispatch success action and show notification
		if (assets.statusCode === 201 || assets.statusCode === 200) {
			yield put(assetGroupActions.addGroupAssetSuccess({ groupID }));

			Notification('success', 'Devices added to Device Groups successfully.');
		} else {
			throw new Error(assets?.error?.message || assets?.error || 'Failed to add devices. Please try again.');
		}
	} catch (error) {
		yield put(assetGroupActions.addGroupAssetError(error));
		Notification('error', error.message);
	}
}

// 🔥Actions here after Add asset into the Group
export function* addGroupAssetSuccess(action) {
	const { groupID } = action.payload;
	yield put(assetGroupActions.loadGroupAssets({ groupID }));
	yield put(permissionsActions.loadPermissions());
}

export function* removeGroupAsset(action) {
	try {
		const { groupID, assetIDs } = action.payload;

		// API request to delete the assets from group
		const assets = yield call(() => SuperFetch.delete(`/deviceGroups/${groupID}/assets`, { assetIDs }));

		if (assets.statusCode === 200) {
			yield put(assetGroupActions.removeGroupAssetSuccess({ groupID }));

			// ✅ Success Notification
			Notification('success', 'Asset(s) have been removed from the group successfully.');
		} else {
			throw Error(assets?.error?.message || 'Failed to delete group assets.');
		}
	} catch (error) {
		yield put(assetGroupActions.removeGroupAssetError(error));
		Notification('error', error.message);
	}
}

// 🔥Actions here after delete assets from group
export function* removeGroupAssetSuccess(action) {
	const { groupID } = action.payload;

	yield put(assetGroupActions.loadGroupAssets({ groupID }));
	yield put(permissionsActions.loadPermissions());
}

// Root saga
export default function* assetGroupSagas() {
	yield takeLatest(assetGroupActions.loadGroups, loadGroups);
	yield takeLatest(assetGroupActions.loadGroupsSuccess, loadGroupsSuccess);
	yield takeLatest(assetGroupActions.loadAssetsGroups, loadAssetsGroups);
	yield takeLatest(assetGroupActions.addGroup, addGroup);
	yield takeLatest(assetGroupActions.addGroupSuccess, addGroupSuccess);
	yield takeLatest(assetGroupActions.updateGroup, updateGroup);
	yield takeLatest(assetGroupActions.updateGroupSuccess, updateGroupSuccess);
	yield takeLatest(assetGroupActions.deleteGroup, deleteGroup);
	yield takeLatest(assetGroupActions.deleteGroupSuccess, deleteGroupSuccess);
	yield takeLatest(assetGroupActions.loadGroupAssets, loadGroupAssets);
	yield takeLatest(assetGroupActions.addGroupAsset, addGroupAsset);
	yield takeLatest(assetGroupActions.addGroupAssetSuccess, addGroupAssetSuccess);
	yield takeLatest(assetGroupActions.removeGroupAsset, removeGroupAsset);
	yield takeLatest(assetGroupActions.removeGroupAssetSuccess, removeGroupAssetSuccess);
}
